import { atomFamily } from "recoil";
import { ApiKeyWithFolders } from "../../../state/types";
import authApi from "../../../backendapi/authApi";

// Access the global db instance
declare const db: any;

type ApiKeyStateParams = { workspaceId: string; includeDisabled?: boolean; folderIds?: string[] };

const syncAuthorizedApiKeysWithFirebaseEffect =
    ({ workspaceId, includeDisabled = false, folderIds = [] }: ApiKeyStateParams) =>
    ({ setSelf }: { setSelf: (value: ApiKeyWithFolders[] | null) => void; trigger: string }) => {
        if (!workspaceId || !db) {
            return;
        }

        // Subscribe to remote storage changes and update the atom value
        const cancelSubscription = db
            .collection("api_keys")
            .where("workspace", "==", workspaceId)
            .onSnapshot(async (snapshot: any) => {
                // Wait for writes to propagate since we're pulling
                // new data from the api keys collection
                if (snapshot.metadata.hasPendingWrites) {
                    return;
                }

                const result: { apiKeys: ApiKeyWithFolders[] } =
                    await authApi.getAuthorizedApiKeysForWorkspace(workspaceId, {
                        includeFolders: true,
                        includeDisabled,
                        folderIds
                    });

                setSelf(result.apiKeys);
            });

        // Cleanup remote storage subscription
        return cancelSubscription;
    };

export const workspaceAuthorizedApiKeysFamily = atomFamily<
    ApiKeyWithFolders[] | null,
    ApiKeyStateParams
>({
    key: "WorkspaceAuthorizedApiKeys",
    default: null,
    effects: ({ workspaceId, includeDisabled, folderIds }) => [
        syncAuthorizedApiKeysWithFirebaseEffect({ workspaceId, includeDisabled, folderIds })
    ]
});

type WorkflowApiKeyStateParams = {
    workflowId: string;
    workspaceId?: string;
    includeFolders?: boolean;
};

const syncWorkflowAuthorizedApiKeysWithFirebaseEffect =
    ({ workflowId, workspaceId, includeFolders = false }: WorkflowApiKeyStateParams) =>
    ({ setSelf }: { setSelf: (value: ApiKeyWithFolders[] | null) => void; trigger: string }) => {
        if (!workflowId || !workspaceId || !db) {
            return;
        }

        // Subscribe to workspace API key changes — any key CRUD in the workspace
        // could affect which keys are authorized for this workflow
        const cancelSubscription = db
            .collection("api_keys")
            .where("workspace", "==", workspaceId)
            .onSnapshot(async (snapshot: any) => {
                if (snapshot.metadata.hasPendingWrites) {
                    return;
                }

                const result: { apiKeys: ApiKeyWithFolders[] } =
                    await authApi.getAuthorizedApiKeysForWorkflow(workflowId, { includeFolders });

                setSelf(result.apiKeys);
            });

        return cancelSubscription;
    };

export const workflowAuthorizedApiKeysFamily = atomFamily<
    ApiKeyWithFolders[] | null,
    WorkflowApiKeyStateParams
>({
    key: "WorkflowAuthorizedApiKeys",
    default: null,
    effects: ({ workflowId, workspaceId, includeFolders }) => [
        syncWorkflowAuthorizedApiKeysWithFirebaseEffect({ workflowId, workspaceId, includeFolders })
    ]
});
